import { MD3LightTheme } from "react-native-paper";

// ═══════════════════════════════════════════════════
// TASKHEALTH DESIGN SYSTEM v2.0 — Token Definitions
//
// Source of truth: docs/mobile/design-system.html
// Single font: Plus Jakarta Sans (no Fraunces)
// Brand colors: Teal #45D2B0 + Pink #E748F5
// No navy palette (was pitch deck only)
// ═══════════════════════════════════════════════════

export const colors = {
    // ── Primary Teal ──
    teal50: "#F0FDF9",
    teal100: "#CCFBEF",
    teal200: "#99F6DE",
    teal300: "#5CEDC4",
    teal400: "#45D2B0", // ★ Brand Primary
    teal500: "#20B89A",
    teal600: "#14957D", // Use for teal TEXT on white (WCAG AA)
    teal700: "#107565",

    // ── Accent Pink ──
    pink50: "#FEF0FF",
    pink100: "#FCD4FD",
    pink200: "#F9A5FB",
    pink300: "#F472F7",
    pink400: "#E748F5", // ★ Brand Accent
    pink500: "#D020DE",
    pink600: "#A818B2",

    // ── Neutrals (purple-tinted for warmth) ──
    gray50: "#FAFBFF",
    gray100: "#F5F4F8",
    gray200: "#EDEAF2",
    gray300: "#DDD8E6",
    gray400: "#B0AECE",
    gray500: "#8B89A8",
    gray600: "#6B6B8D",
    gray700: "#4A4A68",
    gray800: "#2E2B44",
    gray900: "#1F1547",

    // ── Semantic ──
    success50: "#ECFDF5",
    success400: "#34D399",
    success500: "#10B981",
    success600: "#059669",

    warning50: "#FFFBEB",
    warning400: "#FBBF24",
    warning500: "#F59E0B",
    warning600: "#D97706",

    error50: "#FEF2F2",
    error400: "#F87171",
    error500: "#EF4444",
    error600: "#DC2626",

    info50: "#EFF6FF",
    info400: "#60A5FA",
    info500: "#3B82F6",
    info600: "#2563EB",

    // ── Surfaces ──
    background: "#FAFBFF",
    surface: "#FFFFFF",
    surfaceGlass: "rgba(255, 255, 255, 0.72)",
    surfaceGlassBorder: "rgba(255, 255, 255, 0.25)",
    border: "#F0ECF3",
    borderStrong: "#DDD8E6",

    // ── Text ──
    textPrimary: "#1F1547",
    textSecondary: "#6B6B8D",
    textTertiary: "#B0AECE",

    // ── Flat-section design (Section Flattening, May 2026) ──
    // ink is the near-black v2 design uses for selected pills + bold titles;
    // distinct from textPrimary (navy #1F1547) which is the body text color.
    ink: "#0B0B1F",
    // Semantic tint variants for inline range warnings + validation cards.
    warnBg: "rgba(245,158,11,0.10)",
    errBg: "rgba(225,29,72,0.06)",

    // ── Legacy aliases (backward compat for existing screens) ──
    primary: "#45D2B0",
    primaryDark: "#20B89A",
    primaryLight: "#F0FDF9",
    backgroundSecondary: "#F5F4F8",
    surfaceBorder: "#F0ECF3",
    success: "#10B981",
    successLight: "#ECFDF5",
    warning: "#F59E0B",
    warningLight: "#FFFBEB",
    error: "#EF4444",
    errorLight: "#FEF2F2",
    info: "#3B82F6",
    infoLight: "#EFF6FF",
    purple: "#8B5CF6"
};

// ── Icon tint pairs (bg + fg) for category-coded flat icons ──
//
// Used by the POC live activity stream. The categories need hues outside
// the brand palette so they read as visually distinct in a dense list.
// All hex literals live here, never inline in component code (per the
// design-system rule: "ALL colors MUST come from lib/theme.ts").
export const iconTints = {
    teal: { bg: "#E8FAF5", fg: colors.teal600 },
    pink: { bg: "#FCE9FE", fg: colors.pink600 },
    amber: { bg: "#FFF4D6", fg: "#B86E00" },
    neutral: { bg: colors.gray100, fg: colors.gray600 },
    purple: { bg: "#E8E0FF", fg: "#6B4FE0" },
    rose: { bg: "#FFE0E8", fg: "#D63366" },
    sky: { bg: "#DCEEFF", fg: "#2068C8" },
    tealMuted: { bg: colors.teal50, fg: colors.teal600 },
    orange: { bg: "#FFF1D6", fg: "#B86E00" },
    cyan: { bg: "#E5F8FF", fg: "#0B7FB8" },
    tealStrong: { bg: colors.teal100, fg: colors.teal700 }
} as const;

// ── Gradient color pairs (for LinearGradient) ──
export const gradients = {
    brand: ["#45D2B0", "#E748F5"] as const,
    brandSoft: ["rgba(69,210,176,0.10)", "rgba(231,72,245,0.10)"] as const,
    alive: ["#45D2B0", "#5EC4D0", "#B06EE8", "#E748F5"] as const,
    teal: ["#45D2B0", "#20B89A"] as const,
    pink: ["#F472F7", "#E748F5"] as const,
    surface: ["#E8FFF6", "#FAFBFF", "#FEF0FF"] as const,
    cardGlow: ["rgba(69,210,176,0.06)", "rgba(231,72,245,0.06)"] as const,
    success: ["#10B981", "#059669"] as const
};

// ── Shadows ──
export const shadows = {
    xs: {
        shadowColor: "#0F0A28",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 2,
        elevation: 1
    },
    sm: {
        shadowColor: "#0F0A28",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2
    },
    md: {
        shadowColor: "#0F0A28",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 16,
        elevation: 4
    },
    lg: {
        shadowColor: "#0F0A28",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.1,
        shadowRadius: 30,
        elevation: 6
    },
    xl: {
        shadowColor: "#0F0A28",
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.12,
        shadowRadius: 40,
        elevation: 8
    },
    teal: {
        shadowColor: "#45D2B0",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.35,
        shadowRadius: 14,
        elevation: 4
    },
    pink: {
        shadowColor: "#E748F5",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 14,
        elevation: 4
    },
    brand: {
        shadowColor: "#45D2B0",
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.3,
        shadowRadius: 20,
        elevation: 6
    }
};

// ── Spacing ──
export const spacing = {
    xs: 4,
    sm: 8,
    md: 12,
    base: 16,
    lg: 20,
    xl: 24,
    "2xl": 32,
    "3xl": 40,
    "4xl": 48,
    "5xl": 64,
    screenPadding: 16
};

// ── Border Radius ──
export const radius = {
    xs: 6,
    sm: 10,
    md: 14,
    lg: 18,
    xl: 22,
    "2xl": 28,
    full: 9999,
    phone: 40
};

// ── Font Family Constants ──
// Single typeface: Plus Jakarta Sans. No Fraunces.
// Hierarchy through weight + size, not font switching.
export const fonts = {
    light: "PlusJakartaSans_300Light",
    regular: "PlusJakartaSans_400Regular",
    medium: "PlusJakartaSans_500Medium",
    semiBold: "PlusJakartaSans_600SemiBold",
    bold: "PlusJakartaSans_700Bold",
    extraBold: "PlusJakartaSans_800ExtraBold"
};

// ── Typography ──
export const typography = {
    displayLarge: {
        fontSize: 36,
        fontFamily: fonts.extraBold,
        lineHeight: 43,
        letterSpacing: -0.5
    },
    display: {
        fontSize: 28,
        fontFamily: fonts.extraBold,
        lineHeight: 34,
        letterSpacing: -0.3
    },
    h1: {
        fontSize: 22,
        fontFamily: fonts.bold,
        lineHeight: 29
    },
    h2: {
        fontSize: 18,
        fontFamily: fonts.bold,
        lineHeight: 23
    },
    title: {
        fontSize: 16,
        fontFamily: fonts.extraBold,
        lineHeight: 22
    },
    subtitle: {
        fontSize: 14,
        fontFamily: fonts.bold,
        lineHeight: 20
    },
    body: {
        fontSize: 14,
        fontFamily: fonts.medium,
        lineHeight: 21
    },
    bodySmall: {
        fontSize: 13,
        fontFamily: fonts.medium,
        lineHeight: 20
    },
    caption: {
        fontSize: 12,
        fontFamily: fonts.medium,
        lineHeight: 18
    },
    label: {
        fontSize: 10,
        fontFamily: fonts.bold,
        lineHeight: 14,
        letterSpacing: 0.5
    },
    micro: {
        fontSize: 9,
        fontFamily: fonts.bold,
        lineHeight: 13,
        letterSpacing: 1
    }
};

// ── Animation Timing ──
export const timing = {
    instant: 100,
    fast: 200,
    normal: 300,
    slow: 500
};

// ── Visit Type Colors ──
export const visitTypeColors: Record<
    string,
    { bg: string; text: string; gradient: readonly string[] }
> = {
    SOC: { bg: colors.teal50, text: colors.teal400, gradient: gradients.teal },
    REA: { bg: colors.pink50, text: colors.pink400, gradient: gradients.pink },
    SUP: { bg: colors.warning50, text: colors.warning600, gradient: ["#FBBF24", "#F59E0B"] },
    DC: { bg: "#E8EFF7", text: "#0B2D4D", gradient: ["#0B2D4D", "#1A4A72"] },
    ROC: { bg: colors.info50, text: colors.info600, gradient: ["#60A5FA", "#3B82F6"] }
};

// ── Status Colors & Labels ──
// `missed` is not a real visit status; it's surfaced when `missedVisit=true`
// and the nurse is owed travel time (fault=patient or fault=agency).
// Rose hue picked to stay distinct from existing status/visit-type palette.
export const missedVisitColors = {
    accent: "#D63366",
    background: "#FFE0E8",
    // Darker rose for "Missed" text on a white background (WCAG AA).
    text: "#C2185B"
} as const;

export const statusColors: Record<string, string> = {
    pending: colors.warning500,
    assigned: colors.info500,
    in_progress: colors.warning500,
    post_processing: colors.info500,
    completed: colors.success500,
    // Completion vs review are separate axes. under_review/pending_correction are the
    // correction bot's internal write-window — to the RN the visit is still Completed.
    under_review: colors.success500,
    pending_correction: colors.success500,
    sealed: colors.textSecondary,
    cancelled: colors.textSecondary,
    missed: missedVisitColors.accent
};

export const statusLabels: Record<string, string> = {
    pending: "Pending",
    assigned: "Scheduled",
    in_progress: "In Progress",
    post_processing: "Finishing Up",
    completed: "Completed",
    // RN-facing: corrections are an agency-side process; the visit stays Completed.
    under_review: "Completed",
    pending_correction: "Completed",
    sealed: "Sealed",
    cancelled: "Cancelled",
    missed: "Missed"
};

// Badge tokens for a nurse's own visit *requests* (the "My Requests" panel on
// the Explore tab + the inline pending rows on the Scheduled calendar). These
// are distinct from `statusColors`/`statusLabels` above, which describe a
// visit's lifecycle — a request is an offer that is still awaiting an agency
// decision. pending = live offer (amber), cancelled = the nurse withdrew it
// (gray), rejected = the agency declined it (rose). warning600 keeps the amber
// text WCAG-AA on the warning50 background.
export const requestStatusBadge: Record<
    string,
    { label: string; accent: string; background: string }
> = {
    pending: {
        label: "Pending confirmation",
        accent: colors.warning600,
        background: colors.warning50
    },
    accepted: { label: "Approved", accent: colors.success600, background: colors.success50 },
    cancelled: { label: "Cancelled", accent: colors.textSecondary, background: colors.border },
    rejected: {
        label: "Rejected",
        accent: missedVisitColors.accent,
        background: missedVisitColors.background
    }
};

// ── Explore map / cards: the nurse's own relationship to a visit ──
//
// One token set for all four Explore render paths (Google-Maps-JS on web,
// react-native-maps Markers on iOS, the absolute-positioned overlay on Android,
// and the cards list) so a pin can never mean one thing on one platform and
// something else on another — which is exactly what happened when only the web
// path knew about `visit.request`.
//
// Hues deliberately track `requestStatusBadge` above, so a visit reads the same
// on the map as it does in the "My Requests" panel: amber = awaiting a decision,
// rose = the agency declined it, gray = the request was cancelled, green = hers.
// `open` keeps the original navy so an untouched pin looks exactly like it
// always has. Keeping rejected and cancelled on separate hues is deliberate —
// matching the My Requests panel wins over collapsing them, so the two screens
// never disagree about the same visit.
//
// `pin` is the bubble fill (white text sits on it, so all are dark enough for
// that); `badge`/`badgeBg` are the card-list chip pair, matched to the
// requestStatusBadge accent/background convention.
export const exploreRelationStyle: Record<
    "open" | "pending" | "rejected" | "cancelled" | "assigned",
    { label: string | null; pin: string; badge: string; badgeBg: string }
> = {
    open: { label: null, pin: "#0B2D4D", badge: colors.textSecondary, badgeBg: colors.gray100 },
    pending: {
        label: "Requested",
        // warning500, not warning600: this is a fill behind white text, and it
        // matches the amber the web pins already used.
        pin: colors.warning500,
        badge: colors.warning600,
        badgeBg: colors.warning50
    },
    rejected: {
        label: "Rejected",
        pin: missedVisitColors.accent,
        badge: missedVisitColors.accent,
        badgeBg: missedVisitColors.background
    },
    cancelled: {
        label: "Cancelled",
        // Darker than the badge gray so white text on the pin stays legible.
        pin: "#6B6B8D",
        badge: colors.textSecondary,
        badgeBg: colors.border
    },
    assigned: {
        label: "Yours",
        pin: colors.success600,
        badge: colors.success600,
        badgeBg: colors.success50
    }
};

/** The "never opened this one" marker on Explore pins and cards. Brand pink so
 *  it reads as an attention dot and can't be confused with the visit-type dot
 *  inside the pin bubble (teal/pink/amber/navy/blue by type). */
export const exploreUnseenDot = colors.pink400;

// ── Schedule list status display (RN "Scheduled" tab) ──
//
// The Schedule screen collapses the 10 raw visit statuses (+ the `missedVisit`
// flag, + the nurse's pending visit *requests*) into the 6 states a nurse
// actually reads, one hue each. The resolver lives in `lib/schedule-status.ts`
// (`getScheduleStatus`) so it can be unit-tested; this map holds the display
// tokens. Rules baked in: completed AND sealed both read "Completed" (green);
// `missedVisit` always wins and shows only "Missed"; cancelled is struck.
export const scheduleStatusMeta = {
    scheduled: {
        label: "Scheduled",
        rail: colors.gray300,
        text: colors.gray400,
        dashed: false,
        strike: false,
        tint: undefined as string | undefined
    },
    pending: {
        // A request the nurse offered, awaiting agency confirmation (not a visit yet).
        label: "Pending confirmation",
        rail: colors.warning,
        text: "#B26A00",
        dashed: true,
        strike: false,
        tint: "rgba(245,158,11,0.05)" as string | undefined
    },
    in_progress: {
        label: "In progress",
        rail: colors.pink400,
        text: colors.pink600,
        dashed: false,
        strike: false,
        tint: undefined as string | undefined
    },
    completed: {
        label: "Completed",
        rail: colors.teal400,
        text: colors.teal600,
        dashed: false,
        strike: false,
        tint: undefined as string | undefined
    },
    missed: {
        label: "Missed",
        rail: missedVisitColors.accent,
        text: missedVisitColors.text,
        dashed: false,
        strike: false,
        tint: undefined as string | undefined
    },
    cancelled: {
        label: "Cancelled",
        rail: colors.gray300,
        text: colors.gray400,
        dashed: false,
        strike: true,
        tint: undefined as string | undefined
    }
} as const;

export type ScheduleStatusKey = keyof typeof scheduleStatusMeta;

// ── Paper Theme ──
export const paperTheme = {
    ...MD3LightTheme,
    colors: {
        ...MD3LightTheme.colors,
        primary: colors.teal400,
        primaryContainer: colors.teal50,
        onPrimary: "#FFFFFF",
        background: colors.background,
        surface: colors.surface,
        onBackground: colors.textPrimary,
        onSurface: colors.textPrimary,
        outline: colors.border,
        error: colors.error500
    }
};
